import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pillarArticles } from '@/mocks/home';

interface PillarArticlesProps {
  currentLang: string;
}

export default function PillarArticles({ currentLang }: PillarArticlesProps) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const categoryColors: Record<string, string> = {
    Spreadsheet: 'bg-primary-100 text-primary-700',
    Sellers: 'bg-primary-100 text-primary-700',
    Shipping: 'bg-secondary-100 text-secondary-700',
    Warehouse: 'bg-accent-100 text-accent-700',
    Deals: 'bg-secondary-100 text-secondary-700',
    Safety: 'bg-accent-100 text-accent-700',
    Guide: 'bg-secondary-100 text-secondary-700',
    QC: 'bg-accent-100 text-accent-700',
    Review: 'bg-secondary-100 text-secondary-700',
  };

  const openArticle = (slug: string) => {
    navigate(`/${currentLang}/blog/${slug}/`);
    window.scrollTo(0, 0);
  };

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-primary-500 text-xs font-semibold uppercase tracking-[0.15em] mb-3 block">
            Pillar Guides
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900">
            {t('pillar_h2')}
          </h2>
          <p className="text-foreground-500 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-3">
            {t('pillar_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {pillarArticles.map((article) => (
            <div
              key={article.id}
              onClick={() => openArticle(article.slug)}
              className="card-hover group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/40 cursor-pointer flex flex-col"
            >
              <span className={`inline-block self-start px-2.5 py-1 rounded-md text-xs font-semibold mb-3 ${categoryColors[article.category] || 'bg-secondary-100 text-secondary-700'}`}>
                {article.category}
              </span>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                {article.title}
              </h3>
              <p className="text-foreground-500 text-sm leading-relaxed mb-4 line-clamp-3">
                {article.description}
              </p>
              <div className="mt-auto flex items-center justify-between pt-3 border-t border-background-200/40">
                <span className="text-foreground-400 text-xs">Updated {article.updated}</span>
                <span className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium group-hover:text-primary-700 transition-colors">
                  {t('resources_read')}
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}